import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaFacebookF } from 'react-icons/fa';
import {
  FiArrowDown, FiArrowDownLeft, FiArrowDownRight, FiArrowLeft, FiArrowRight,
  FiArrowUp, FiArrowUpLeft, FiArrowUpRight, FiBell, FiCamera, FiCheck,
  FiChevronsLeft, FiChevronsRight, FiMapPin, FiMoon, FiRefreshCw, FiSearch, FiSun,
} from 'react-icons/fi';
import './SettingsWorkspace.css';

const PANELS = [
  { id: 'camera', label: 'Camera controls', icon: FiCamera },
  { id: 'alerts', label: 'Alerts', icon: FiBell },
  { id: 'scenic', label: 'Sunrise & sunset', icon: FiSun },
];

export default function SettingsWorkspace({ clients, selectedClient, cameras, selectedCameraId,
  onCameraSelect, onRefresh, facebookSummary, cameraControls, alerts, scenic, facebook }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [panel, setPanel] = useState('camera');
  const [search, setSearch] = useState('');
  const [params] = useSearchParams();
  const visibleClients = clients.filter((client) => (client.name || client.id)
    .toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className={`settingsWorkspace${sidebarCollapsed ? ' settingsWorkspace--collapsed' : ''}`}>
      <aside className="settingsWorkspace__sidebar" aria-label="Locations">
        <div className="settingsWorkspace__sidebarHeading">
          <h2 hidden={sidebarCollapsed}>Locations</h2>
          <button type="button" className="settingsWorkspace__sidebarToggle"
            aria-label={sidebarCollapsed ? 'Show locations' : 'Hide locations'}
            aria-expanded={!sidebarCollapsed} aria-controls="settings-location-panel"
            title={sidebarCollapsed ? 'Show locations' : 'Hide locations'}
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}>
            {sidebarCollapsed ? <FiChevronsRight aria-hidden="true" /> : <FiChevronsLeft aria-hidden="true" />}
            <span>{sidebarCollapsed ? 'Locations' : 'Hide'}</span>
          </button>
        </div>
        <div id="settings-location-panel" className="settingsWorkspace__sidebarContent" hidden={sidebarCollapsed}>
        <label className="settingsWorkspace__search"><FiSearch aria-hidden="true" />
          <input aria-label="Search locations" placeholder="Search locations…" value={search}
            onChange={(event) => setSearch(event.target.value)} />
        </label>
        <nav className="settingsWorkspace__locations" aria-label="Choose location">
          {visibleClients.map((client) => {
            const next = new URLSearchParams(params);
            next.set('location', client.id);
            return <Link key={client.id} to={`?${next.toString()}`}
              aria-current={selectedClient?.id === client.id ? 'page' : undefined}
              className="settingsWorkspace__location"><FiMapPin aria-hidden="true" />
              <span>{client.name || client.id}</span></Link>;
          })}
          {!visibleClients.length && <p className="settingsInlineMeta">{clients.length ? 'No matching locations.' : 'No locations available.'}</p>}
        </nav>
        <div className="settingsWorkspace__facebook">
          <FaFacebookF className="settingsWorkspace__facebookIcon" aria-hidden="true" />
          <div><strong>Facebook Page</strong>
            <span className={`settingsWorkspace__connection${facebookSummary.connected ? ' is-connected' : ''}`}>
              {facebookSummary.connected && <FiCheck aria-hidden="true" />}
              {facebookSummary.loading ? 'Checking…' : facebookSummary.error ? 'Check connection' : facebookSummary.connected ? 'Connected' : 'Not connected'}
            </span>
            {facebookSummary.pageName && <p>{facebookSummary.pageName}</p>}
            <small>Account-wide · Nothing posts automatically.</small>
            <button className="settingsButton settingsButton--ghost" type="button" onClick={() => setPanel('facebook')}
              aria-pressed={panel === 'facebook'}>Manage</button>
          </div>
        </div>
        </div>
      </aside>
      <main className="settingsWorkspace__main">
        <header className="settingsWorkspace__heading">
          <div><span className="adminSettings__eyebrow">Settings</span>
            <h1>{panel === 'facebook' ? 'Facebook Page' : selectedClient?.name || selectedClient?.id || 'Your locations'}</h1>
          </div>
          <div className="settingsWorkspace__headingActions">
            {panel !== 'facebook' && <label>Camera
              <select className="settingsInput" aria-label="Camera" value={selectedCameraId}
                disabled={!cameras.length} onChange={(event) => onCameraSelect(event.target.value)}>
                {!cameras.length && <option value="">No cameras</option>}
                {cameras.map((camera) => <option key={camera.id} value={camera.id}>{camera.displayName || camera.id}</option>)}
              </select></label>}
            <button type="button" className="settingsButton settingsButton--ghost" onClick={onRefresh}><FiRefreshCw /> Refresh</button>
          </div>
        </header>
        <nav className="settingsWorkspace__tabs" aria-label="Settings sections">
          {PANELS.map(({ id, label, icon: Icon }) => <button key={id} type="button"
            aria-pressed={panel === id} onClick={() => setPanel(id)}><Icon aria-hidden="true" />{label}</button>)}
        </nav>
        <div hidden={panel !== 'camera'}>{cameraControls}</div>
        <div hidden={panel !== 'alerts'}>{alerts}</div>
        <div hidden={panel !== 'scenic'}>{scenic}</div>
        <div hidden={panel !== 'facebook'}>{facebook}</div>
      </main>
    </div>
  );
}

const DIRECTION_ICONS = { nw: FiArrowUpLeft, n: FiArrowUp, ne: FiArrowUpRight,
  w: FiArrowLeft, e: FiArrowRight, sw: FiArrowDownLeft, s: FiArrowDown, se: FiArrowDownRight };
const GRID_POSITIONS = { nw: 1, n: 2, ne: 3, w: 4, e: 6, sw: 7, s: 8, se: 9 };

export function CameraMovePad({ buttons, disabled, onMove }) {
  const [step, setStep] = useState('step');
  return <>
    <div className="settingsWorkspace__segments" role="group" aria-label="Movement distance">
      <button type="button" aria-pressed={step === 'step'} onClick={() => setStep('step')}>30% (smaller move)</button>
      <button type="button" aria-pressed={step === 'edge'} onClick={() => setStep('edge')}>Frame edge</button>
    </div>
    <div className="settingsWorkspace__movePad" role="group" aria-label="Camera move pad">
      {buttons.filter((button) => button.tone === step).map((button) => {
        const direction = button.id.split('-').pop();
        const Icon = DIRECTION_ICONS[direction];
        const position = GRID_POSITIONS[direction];
        return <button type="button" key={button.id} aria-label={button.title} title={button.title}
          disabled={disabled} onClick={() => onMove(button)}
          style={{ gridColumn: (position - 1) % 3 + 1, gridRow: Math.ceil(position / 3) }}>
          <Icon aria-hidden="true" />
        </button>;
      })}
    </div>
  </>;
}

export function SettingsPresets({ presets, loading, disabled, cameraId, cameraName, onSelect }) {
  const [period, setPeriod] = useState('day');
  const [search, setSearch] = useState('');
  const visible = presets.filter((preset) => {
    // Older presets without a schedule remain reachable in both periods.
    const schedule = String(preset.whenIsActive || '').trim().toLowerCase();
    const matchesPeriod = !['day', 'night'].includes(schedule) || schedule === period;
    return matchesPeriod && `${preset.name || ''} ${preset.id}`.toLowerCase().includes(search.trim().toLowerCase());
  });
  return <>
    <label className="settingsWorkspace__search"><FiSearch aria-hidden="true" />
      <input aria-label="Search presets" placeholder="Search presets…" value={search} onChange={(event) => setSearch(event.target.value)} />
    </label>
    <div className="settingsWorkspace__segments" role="group" aria-label="Preset period">
      <button type="button" aria-pressed={period === 'day'} onClick={() => setPeriod('day')}><FiSun /> Day presets</button>
      <button type="button" aria-pressed={period === 'night'} onClick={() => setPeriod('night')}><FiMoon /> Night presets</button>
    </div>
    <div key={`${period}:${search}`} className="settingsWorkspace__presets" role="region" aria-label={`${period === 'day' ? 'Day' : 'Night'} preset positions`} tabIndex={0}>
      {!cameraId ? <p>Pick a camera to see its presets.</p> : loading ? <p>Loading presets…</p> : !visible.length ? <p>No matching presets.</p> : visible.map((preset) => (
        <button type="button" key={preset.id} disabled={disabled} onClick={() => onSelect(preset)}
          title={`Move ${cameraName || cameraId} to ${preset.name || preset.id}`}>{preset.name || preset.id}</button>
      ))}
    </div>
    <small className="settingsWorkspace__presetCount">{loading ? 'Loading…' : `${visible.length} positions · Scroll to browse`}</small>
  </>;
}
